import { DocumentMinusIcon } from "@heroicons/react/24/solid";
import { t } from "@lingui/core/macro";
import { skipToken } from "@reduxjs/toolkit/query";
import { createFileRoute } from "@tanstack/react-router";
import React from "react";
import { useSelector } from "react-redux";

import { selectMaybeActiveAccountEid } from "@/redux/app/appSelectors";
import { useGetAccountInfoQuery } from "@/shared/api/account";
import ApiErrorBoundary from "@/shared/components/route-state/ApiErrorBoundary";
import { Pagination } from "@/shared/components/ui/pagination";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { dateBetween, formatDateMonthYearFull, fromStringToDate } from "@/util/dateUtils";

import { StatementListItem } from "./-components/StatementListItem";

const TASKS_PER_PAGE = 6;

const StatementSkeleton = () => <Skeleton className="h-[875px] rounded-2xl" />;

const StatementEmptyState = () => {
  return (
    <div className="absolute inset-0 m-auto flex flex-col items-center justify-center gap-2">
      <DocumentMinusIcon className="h-10 w-10 text-primary-light" />
      <p className="mx-4 max-w-lg text-center [textWrap:balance]">{t`No statements found. New clients can expect their first statement within one month of account opening.`}</p>
    </div>
  );
};

function StatementListView() {
  const [page, setPage] = React.useState(1);
  const eid = useSelector(selectMaybeActiveAccountEid);

  const accountInfoQuery = useGetAccountInfoQuery(eid || skipToken);
  const { data, isLoading } = accountInfoQuery;

  if (isLoading) {
    return <StatementSkeleton />;
  }

  if (!data?.first_statement_date || !data?.last_statement_date) {
    return <StatementEmptyState />;
  }

  const DEFAULTSTARTDATE = new Date(Date.UTC(2022, 4, 1));
  const statementStartDate = fromStringToDate(data.first_statement_date);
  const oldestStartDate = DEFAULTSTARTDATE > statementStartDate ? DEFAULTSTARTDATE : statementStartDate;
  const lastStatementDate = fromStringToDate(data.last_statement_date);
  const allMonthsInRange = dateBetween(oldestStartDate, lastStatementDate, 1200);
  const statementDates = allMonthsInRange.slice(-12).reverse();

  return (
    <ApiErrorBoundary
      translatedErrorMessage={t`Unable to load account details`}
      shouldRetry
      queries={[accountInfoQuery]}
    >
      {statementDates.length > 0 ? (
        <div className="flex flex-col gap-2">
          <span>
            <h2 className="text-xl font-medium text-primary">{t`Statements`}</h2>
            <hr />
          </span>
          <span>
            {statementDates.slice((page - 1) * TASKS_PER_PAGE, page * TASKS_PER_PAGE).map((date) => (
              <StatementListItem
                dateText={formatDateMonthYearFull(date)}
                key={`statement-${eid}-${date.getUTCMonth() + 1}-${date.getUTCFullYear()}`}
                eid={eid}
                month={date.getUTCMonth() + 1}
                year={date.getUTCFullYear()}
              />
            ))}
          </span>
          {statementDates.length > TASKS_PER_PAGE && (
            <Pagination page={page} count={Math.ceil(statementDates.length / TASKS_PER_PAGE)} onChange={setPage} />
          )}
        </div>
      ) : (
        <StatementEmptyState />
      )}
    </ApiErrorBoundary>
  );
}

export const Route = createFileRoute("/_authenticated/documents/statement")({
  component: StatementListView,
  context: () => ({ permissions: "document.statement" }),
});
